import React, { useState, useEffect } from 'react';
import { generateChartInterpretations } from '../lib/interpretationGenerator';
import './AstrologyChart.css';

/** Fallback backend when same-origin fails (env or last-resort). */
const FALLBACK_API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const LAST_RESORT_BACKEND = 'https://astralmoon-backend.vercel.app';

function getApiBase() {
  return FALLBACK_API_BASE;
}

function isDebugMode() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('debug') === '1' || localStorage.getItem('astro-debug') === '1';
}

const SIGNS = [
  { value: 'aries', label: 'Oinas (Aries)' },
  { value: 'taurus', label: 'Härkä (Taurus)' },
  { value: 'gemini', label: 'Kaksoset (Gemini)' },
  { value: 'cancer', label: 'Rapu (Cancer)' },
  { value: 'leo', label: 'Leijona (Leo)' },
  { value: 'virgo', label: 'Neitsyt (Virgo)' },
  { value: 'libra', label: 'Vaaka (Libra)' },
  { value: 'scorpio', label: 'Skorpioni (Scorpio)' },
  { value: 'sagittarius', label: 'Jousimies (Sagittarius)' },
  { value: 'capricorn', label: 'Kauris (Capricorn)' },
  { value: 'aquarius', label: 'Vesimies (Aquarius)' },
  { value: 'pisces', label: 'Kalat (Pisces)' },
];

function signToDate(signValue) {
  const dates = {
    aries: '2000-03-21', taurus: '2000-04-20', gemini: '2000-05-21', cancer: '2000-06-21',
    leo: '2000-07-23', virgo: '2000-08-23', libra: '2000-09-23', scorpio: '2000-10-24',
    sagittarius: '2000-11-23', capricorn: '2000-12-22', aquarius: '2000-01-20', pisces: '2000-02-19',
  };
  return dates[signValue] || '2000-03-21';
}

function chartApiToInterpretationData(apiChart) {
  if (!apiChart || !apiChart.planets) return null;
  const planets = apiChart.planets;
  const sun = planets.find((p) => p.key === 'Sun');
  const moon = planets.find((p) => p.key === 'Moon');
  const asc = apiChart.angles?.asc;
  const positions = {};
  planets.forEach((p) => {
    const deg = (p.deg != null ? p.deg : 0) + (p.min != null ? p.min : 0) / 60;
    positions[p.key] = { sign: p.sign, degree: deg };
  });
  const houses = {};
  if (Array.isArray(apiChart.houses)) {
    apiChart.houses.forEach((h) => {
      houses[`House ${h.house}`] = h.sign;
    });
  }
  return {
    sunSign: sun?.sign || null,
    moonSign: moon?.sign || null,
    risingSign: asc?.sign || asc || null,
    positions,
    houses: Object.keys(houses).length ? houses : null,
    aspects: Array.isArray(apiChart.aspects) ? apiChart.aspects : [],
  };
}

const SYMBOLS = { Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓' };

const AstrologyChart = () => {
  const [tab, setTab] = useState('personality');

  const [personalitySign, setPersonalitySign] = useState('aries');
  const [personalityData, setPersonalityData] = useState(null);
  const [personalityLoading, setPersonalityLoading] = useState(false);
  const [personalityError, setPersonalityError] = useState('');
  const [personalityDiagnostics, setPersonalityDiagnostics] = useState(null);

  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('12:00');
  const [locationQuery, setLocationQuery] = useState('');
  const [timezone, setTimezone] = useState('Europe/Helsinki');
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState('');
  const [chartReport, setChartReport] = useState(null);
  const [geoResult, setGeoResult] = useState(null);

  const [compatDateA, setCompatDateA] = useState('2000-03-21');
  const [compatTimeA, setCompatTimeA] = useState('12:00');
  const [compatPlaceA, setCompatPlaceA] = useState('');
  const [compatDateB, setCompatDateB] = useState('2000-07-23');
  const [compatTimeB, setCompatTimeB] = useState('12:00');
  const [compatPlaceB, setCompatPlaceB] = useState('');
  const [compatData, setCompatData] = useState(null);
  const [compatLoading, setCompatLoading] = useState(false);
  const [compatError, setCompatError] = useState('');

  useEffect(() => {
    document.title = 'Syntymäkartta ja yhteensopivuus | Astral Moon Magic';
  }, []);

  useEffect(() => {
    if (tab !== 'personality') return;
    let cancelled = false;
    setPersonalityLoading(true);
    setPersonalityError('');
    setPersonalityData(null);
    const payload = {
      sign: personalitySign,
      lang: 'fi',
      tone: 'neutraali',
    };
    const method = 'POST';

    function devLog(label, extra) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[AstrologyChart] personality ' + label, { method, ...extra });
      }
    }

    function doFetch(url) {
      return fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }

    function captureDiag(res, requestUrl, usedLastResort) {
      if (cancelled) return;
      setPersonalityDiagnostics({
        requestUrl,
        method: 'POST',
        status: res.status,
        server: res.headers.get('server') || null,
        contentType: res.headers.get('content-type') || null,
        xVercelId: res.headers.get('x-vercel-id') || null,
        xMatchedPath: res.headers.get('x-matched-path') || null,
        usedLastResort: usedLastResort || false,
      });
    }

    const sameOriginUrl = '/api/personality';
    devLog('request', { url: sameOriginUrl });
    setPersonalityData(null);

    (async function run() {
      try {
        let res = await doFetch(sameOriginUrl);
        const ct = res.headers.get('content-type') || '';
        captureDiag(res, sameOriginUrl, false);
        devLog('response', { url: sameOriginUrl, status: res.status, contentType: ct, server: res.headers.get('server'), xVercelId: res.headers.get('x-vercel-id') });
        if (res.ok && ct.toLowerCase().includes('application/json')) {
          const data = await res.json();
          if (!cancelled) { setPersonalityData(data.error ? null : data); if (data.error) setPersonalityError(data.error); else setPersonalityError(''); }
          return;
        }
        const needsFallback = res.status === 404 || res.status === 405 || (res.status >= 500 && res.status < 600);
        const fallbackUrl = FALLBACK_API_BASE ? (FALLBACK_API_BASE.replace(/\/$/, '') + '/api/personality') : null;
        if (needsFallback && fallbackUrl) {
          res = await doFetch(fallbackUrl);
          captureDiag(res, fallbackUrl, false);
          const fallbackCt = res.headers.get('content-type') || '';
          if (res.ok && fallbackCt.toLowerCase().includes('application/json')) {
            const data = await res.json();
            if (!cancelled) { setPersonalityData(data.error ? null : data); if (data.error) setPersonalityError(data.error); else setPersonalityError(''); }
            return;
          }
        }
        if (needsFallback) {
          const lastResortUrl = LAST_RESORT_BACKEND + '/api/personality';
          res = await doFetch(lastResortUrl);
          captureDiag(res, lastResortUrl, true);
          const lastCt = res.headers.get('content-type') || '';
          if (res.ok && lastCt.toLowerCase().includes('application/json')) {
            const data = await res.json();
            if (!cancelled) { setPersonalityData(data.error ? null : data); if (data.error) setPersonalityError(data.error); else setPersonalityError(''); }
            return;
          }
        }
        let message = `Palvelinvirhe (${res.status})`;
        try {
          const text = await res.text();
          if (text) message += ` – ${text.slice(0, 200)}`;
        } catch {
          // ignore
        }
        throw new Error(message);
      } catch (err) {
        if (!cancelled) {
          const msg = err.message || '';
          const is405 = msg.includes('405');
          const is404 = msg.includes('404');
          const friendly = is405
            ? 'Palvelin ei tue tätä pyyntöä. Yritä myöhemmin uudelleen.'
            : is404
              ? 'API-osote ei löytynyt. Tarkista verkkosivun asetukset.'
              : msg.includes('500') || msg.includes('503')
                ? 'Palvelinvirhe. Yritä hetken kuluttua uudelleen.'
                : msg.includes('429')
                  ? 'Liian monta pyyntöä. Odota hetki ja yritä uudelleen.'
                  : msg || 'Verkkovirhe. Yritä uudelleen.';
          setPersonalityError(friendly);
        }
      } finally {
        if (!cancelled) setPersonalityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, personalitySign]);

  const handleGeocode = () => {
    if (!locationQuery.trim()) return;
    setChartError('');
    fetch((getApiBase() || '') + '/api/geocode', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: locationQuery.trim() }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.ok && data.results && data.results.length > 0) {
          const r = data.results[0];
          setGeoResult({ lat: r.lat, lng: r.lon, display: r.display_name });
        } else {
          setGeoResult(null);
          setChartError('Paikkaa ei löytynyt. Kokeile toista hakusanaa.');
        }
      })
      .catch(() => setChartError('Paikan haku epäonnistui.'));
  };

  const handleGenerateReport = () => {
    if (!birthDate) {
      setChartError('Syötä syntymäpäivä.');
      return;
    }
    const dateStr = birthDate.includes('-') ? birthDate : birthDate.split('.').reverse().join('-');
    const timeStr = birthTime.includes(':') ? birthTime : `${birthTime.slice(0, 2)}:${birthTime.slice(2)}`;

    const base = getApiBase();
    if (base) {
      setChartLoading(true);
      setChartError('');
      setChartReport(null);
      const birthPayload = {
        person: { date: dateStr, time: timeStr, place: locationQuery.trim() || undefined },
        lang: 'fi',
        tone: 'neutraali',
      };
      const birthUrl = `${base}/api/birth-chart`;
      if (process.env.NODE_ENV === 'development') {
        console.debug('[AstrologyChart] birth-chart request', { url: birthUrl, method: 'POST' });
      }
      fetch(birthUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(birthPayload),
      })
        .then((res) => {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[AstrologyChart] birth-chart response', { status: res.status, contentType: res.headers.get('content-type') });
          }
          return res.json();
        })
        .then((data) => {
          if (data.error) setChartError(data.error);
          else setChartReport({ type: 'new', data });
        })
        .catch((err) => setChartError(err.message || 'Verkkovirhe'))
        .finally(() => setChartLoading(false));
      return;
    }

    if (!geoResult || geoResult.lat == null) {
      setChartError('Hae ensin syntymäpaikka (paikka ja Hae) tai käytä backend-URL:ia.');
      return;
    }
    setChartLoading(true);
    setChartError('');
    setChartReport(null);
    fetch((getApiBase() || '') + '/api/chart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        birth_date: dateStr,
        birth_time: timeStr,
        timezone: timezone || 'Europe/Helsinki',
        lat: geoResult.lat,
        lng: geoResult.lng,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (!data.ok) {
          setChartError(data.details || data.message || data.error || 'Kartan laskenta epäonnistui');
          return;
        }
        const chartData = chartApiToInterpretationData(data);
        if (!chartData) {
          setChartError('Kartan muotoilu epäonnistui');
          return;
        }
        const interpretations = generateChartInterpretations(chartData);
        setChartReport({ type: 'legacy', raw: data, chartData, interpretations });
      })
      .catch((err) => setChartError(err.message || 'Verkkovirhe'))
      .finally(() => setChartLoading(false));
  };

  const handleCompatibility = () => {
    setCompatLoading(true);
    setCompatError('');
    setCompatData(null);
    const compatPayload = {
      personA: { date: compatDateA, time: compatTimeA, place: compatPlaceA || undefined },
      personB: { date: compatDateB, time: compatTimeB, place: compatPlaceB || undefined },
      lang: 'fi',
      tone: 'neutraali',
    };
    const base = getApiBase();
    const url = base ? `${base}/api/compatibility` : '/api/compatibility';
    if (process.env.NODE_ENV === 'development') {
      console.debug('[AstrologyChart] compatibility request', { url, method: 'POST' });
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(compatPayload),
    })
      .then((res) => {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[AstrologyChart] compatibility response', { status: res.status, contentType: res.headers.get('content-type') });
        }
        return res.json();
      })
      .then((data) => {
        if (data.error) setCompatError(data.error);
        else setCompatData(data);
      })
      .catch((err) => setCompatError(err.message || 'Verkkovirhe'))
      .finally(() => setCompatLoading(false));
  };

  return (
    <div className="astrology-chart-page">
      <div className="container">
        <header className="astrology-chart-hero">
          <h1>Syntymäkartta ja astrologiatyökalut</h1>
          <p>Personallisuusanalyysi, syntymäkartan raportti ja yhteensopivuus (Zodii-tyyli)</p>
        </header>

        <nav className="astrology-chart-tabs">
          <button type="button" className={tab === 'personality' ? 'active' : ''} onClick={() => setTab('personality')}>
            Personallisuusanalyysi
          </button>
          <button type="button" className={tab === 'report' ? 'active' : ''} onClick={() => setTab('report')}>
            Syntymäkartan raportti
          </button>
          <button type="button" className={tab === 'compatibility' ? 'active' : ''} onClick={() => setTab('compatibility')}>
            Yhteensopivuus
          </button>
        </nav>

        {tab === 'personality' && (
          <section className="astrology-chart-card">
            <h2>Personallisuusanalyysi (aurinkomerkki)</h2>
            <select className="personality-select" value={personalitySign} onChange={(e) => setPersonalitySign(e.target.value)} aria-label="Valitse merkki">
              {SIGNS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            {personalityLoading && <p className="astrology-chart-loading">Ladataan…</p>}
            {personalityError && <p className="astrology-chart-error">{personalityError}</p>}
            {isDebugMode() && personalityDiagnostics && (
              <div className="astrology-diagnostics" style={{ marginTop: '1rem', padding: '0.75rem', background: '#1a1a2e', borderRadius: '6px', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                <strong>Diagnostics (personality)</strong>
                <div style={{ marginTop: '0.5rem' }}>
                  <div>request: {personalityDiagnostics.method} {personalityDiagnostics.requestUrl}</div>
                  <div>status: {personalityDiagnostics.status}</div>
                  <div>server: {personalityDiagnostics.server ?? '—'}</div>
                  <div>content-type: {personalityDiagnostics.contentType ?? '—'}</div>
                  <div>x-vercel-id: {personalityDiagnostics.xVercelId ?? '—'}</div>
                  <div>x-matched-path: {personalityDiagnostics.xMatchedPath ?? '—'}</div>
                  {personalityDiagnostics.usedLastResort && <div style={{ color: '#b0b0ff' }}>used last-resort backend</div>}
                </div>
              </div>
            )}
            {personalityData && !personalityLoading && (
              <div className="personality-result">
                <div className="sign-header">
                  <span className="sign-symbol" aria-hidden>{SYMBOLS[personalityData.zodiac_sign] || '♈'}</span>
                  <div>
                    <div className="sign-name">
                      {personalityData.zodiac_sign
                        ? `${personalityData.zodiac_sign} – persoonallisuus`
                        : 'Personallisuus'}
                    </div>
                  </div>
                </div>
                <p>
                  <strong>Elementti:</strong>{' '}
                  {personalityData.element || '–'}
                </p>
                <p>
                  <strong>Hallitseva planeetta:</strong>{' '}
                  {personalityData.ruling_planet || '–'}
                </p>
                {personalityData.personality?.description && (
                  <p>{personalityData.personality.description}</p>
                )}
                {personalityData.personality?.strengths &&
                  personalityData.personality.strengths.length > 0 && (
                  <>
                    <p><strong>Vahvuudet</strong></p>
                    <ul>
                      {personalityData.personality.strengths.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </>
                )}
                {personalityData.personality?.weaknesses &&
                  personalityData.personality.weaknesses.length > 0 && (
                  <>
                    <p><strong>Heikkoudet</strong></p>
                    <ul>
                      {personalityData.personality.weaknesses.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {tab === 'report' && (
          <section className="astrology-chart-card">
            <h2>Luo syntymäkartan raportti</h2>
            <p style={{ color: '#a090c0', marginBottom: '1rem', fontSize: '0.95rem' }}>
              Syötä syntymäpäivä, -aika ja -paikka. Backend laskee kartan (paikka valinnainen; backend geokoodaa).
            </p>
            <div className="chart-form-grid">
              <div>
                <label htmlFor="birth-date">Syntymäpäivä (YYYY-MM-DD)</label>
                <input id="birth-date" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
              </div>
              <div>
                <label htmlFor="birth-time">Syntymäaika (HH:MM)</label>
                <input id="birth-time" type="text" placeholder="12:00" value={birthTime} onChange={(e) => setBirthTime(e.target.value)} />
              </div>
              <div>
                <label htmlFor="location">Syntymäpaikka (kaupunki, maa)</label>
                <input id="location" type="text" placeholder="Helsinki, Suomi" value={locationQuery} onChange={(e) => setLocationQuery(e.target.value)} />
                {!getApiBase() && (
                  <button type="button" className="btn-generate" style={{ marginTop: '0.5rem' }} onClick={handleGeocode}>Hae</button>
                )}
                {geoResult && <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#a0a0c0' }}>✓ {geoResult.display}</p>}
              </div>
            </div>
            <div className="chart-form-actions">
              <button type="button" className="btn-generate" onClick={handleGenerateReport} disabled={chartLoading}>
                {chartLoading ? 'Lasketaan…' : 'Luo raportti'}
              </button>
            </div>
            {chartError && <p className="astrology-chart-error">{chartError}</p>}
            {chartReport && chartReport.type === 'new' && (
              <div className="chart-report-result">
                <p>{chartReport.data.summary}</p>
                <h3>Planeetat</h3>
                <div className="big-three">
                  <div className="big-three-item"><strong>Aurinko</strong> {chartReport.data.placements?.sun}</div>
                  <div className="big-three-item"><strong>Kuu</strong> {chartReport.data.placements?.moon}</div>
                  <div className="big-three-item"><strong>Nouseva</strong> {chartReport.data.placements?.asc}</div>
                </div>
                {chartReport.data.highlights && chartReport.data.highlights.length > 0 && (
                  <>
                    <h3>Kohokohdat</h3>
                    <ul>
                      {chartReport.data.highlights.map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
            {chartReport && chartReport.type === 'legacy' && (
              <div className="chart-report-result">
                <h3>Big Three</h3>
                <div className="big-three">
                  <div className="big-three-item"><strong>Aurinkomerkki</strong> {chartReport.chartData.sunSign}</div>
                  <div className="big-three-item"><strong>Kuumerkki</strong> {chartReport.chartData.moonSign}</div>
                  <div className="big-three-item"><strong>Nousumerkki</strong> {chartReport.chartData.risingSign?.sign || chartReport.chartData.risingSign}</div>
                </div>
                <h3>Tulkinta</h3>
                {chartReport.interpretations.map((item, i) => (
                  <div key={i} className="interpretation-block">
                    <h3>{item.title}</h3>
                    <p>{item.content}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === 'compatibility' && (
          <section className="astrology-chart-card">
            <h2>Yhteensopivuus (kaksi henkilöä)</h2>
            <div className="compat-selects" style={{ flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label>Henkilö A: syntymäpäivä</label>
                <input type="date" value={compatDateA} onChange={(e) => setCompatDateA(e.target.value)} />
                <input type="text" placeholder="Aika HH:MM" value={compatTimeA} onChange={(e) => setCompatTimeA(e.target.value)} style={{ marginLeft: '0.5rem' }} />
                <input type="text" placeholder="Paikka" value={compatPlaceA} onChange={(e) => setCompatPlaceA(e.target.value)} style={{ marginLeft: '0.5rem', width: '200px' }} />
              </div>
              <div>
                <label>Henkilö B: syntymäpäivä</label>
                <input type="date" value={compatDateB} onChange={(e) => setCompatDateB(e.target.value)} />
                <input type="text" placeholder="Aika HH:MM" value={compatTimeB} onChange={(e) => setCompatTimeB(e.target.value)} style={{ marginLeft: '0.5rem' }} />
                <input type="text" placeholder="Paikka" value={compatPlaceB} onChange={(e) => setCompatPlaceB(e.target.value)} style={{ marginLeft: '0.5rem', width: '200px' }} />
              </div>
              <button type="button" className="btn-generate" onClick={handleCompatibility} disabled={compatLoading}>
                {compatLoading ? 'Tarkistan…' : 'Tarkista yhteensopivuus'}
              </button>
            </div>
            {compatError && <p className="astrology-chart-error">{compatError}</p>}
            {compatData && !compatLoading && (
              <div className="compat-result">
                <p className="summary">{compatData.overall}</p>
                {compatData.strengths && compatData.strengths.length > 0 && (
                  <>
                    <p><strong>Vahvuudet</strong></p>
                    <ul>
                      {compatData.strengths.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </>
                )}
                {compatData.challenges && compatData.challenges.length > 0 && (
                  <>
                    <p><strong>Haasteet</strong></p>
                    <ul>
                      {compatData.challenges.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </>
                )}
                {compatData.advice && compatData.advice.length > 0 && (
                  <>
                    <p><strong>Vinkit</strong></p>
                    <ul>
                      {compatData.advice.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </section>
        )}

        <p className="astrology-chart-disclaimer">
          Nämä työkalut on tarkoitettu viihde- ja itsetuntemuksen tukemiseen. Älä tee elämänpäätöksiä pelkästään astrologian perusteella.
        </p>
      </div>
    </div>
  );
};

export default AstrologyChart;
